import React from 'react';
import { Link } from 'react-router-dom';

function Solutions() {
  const keyFeatures = [
    {
      title: "Project Management System",
      icon: "fas fa-project-diagram",
      description: "Create, assign, and manage projects with ease. Track billable and non-billable time, set milestones, monitor real-time progress, maintain project budgets and expenses, view deadlines through an integrated project calendar, and improve team collaboration with project discussions and task comments.",
      seoKeywords: "project management software Malaysia, team collaboration tool, task tracking CRM"
    },
    {
      title: "Invoicing & Estimates (Billing Automation)",
      icon: "fas fa-file-invoice-dollar",
      description: "Create branded invoices and export them as PDFs. Support for recurring invoices and automated billing cycles. Convert estimates, quotations, and proposals into invoices instantly. Accept online payments via PayPal, Stripe, and other gateways. Track invoice status, partial payments, credit notes, and more.",
      seoKeywords: "invoicing software Malaysia, billing automation system, online payment CRM"
    },
    {
      title: "Proposal Builder",
      icon: "fas fa-file-alt",
      description: "Create proposal documents with images, videos, tables, and formatting. Allow clients to comment, request changes, or approve online. Full e-signature support for faster deal closure. Track proposal views, acceptance, and client activity.",
      seoKeywords: "proposal management system, digital proposal builder, e-signature CRM"
    },
    {
      title: "Lead Management & Sales Pipeline",
      icon: "fas fa-users",
      description: "Drag-and-drop lead movement through pipeline stages. Automatic lead capture from emails or web forms. Assign leads to specific sales agents. Convert leads to customers with a single click. Track communication history, notes, files, and follow-ups.",
      seoKeywords: "lead management system, sales CRM Malaysia, pipeline tracking tool"
    },
    {
      title: "Expenses & Payments Tracking",
      icon: "fas fa-money-bill-wave",
      description: "Log daily expenses and categorize them. Set recurring expenses such as supplier fees or subscriptions. Track payment history and outstanding balances. Receive online payments directly from clients.",
      seoKeywords: "expense tracking software, financial management CRM, online payment tracking"
    },
    {
      title: "Customer Support Ticketing System",
      icon: "fas fa-headset",
      description: "Convert emails into support tickets. Pre-defined replies for faster resolutions. Staff internal notes for collaboration. Attach files, screenshots, documents. Track SLAs, ticket priority, status, and response times.",
      seoKeywords: "customer support system, helpdesk CRM, ticket management software"
    },
    {
      title: "Contracts & E-Signature Module",
      icon: "fas fa-file-signature",
      description: "Create contract templates with custom fields. Send contracts for approval electronically. E-signature support for proposals, estimates, invoices, and contracts. Track contract start/end dates and renewals.",
      seoKeywords: "contract management system, digital signature CRM, e-contract software"
    },
    {
      title: "Survey Builder",
      icon: "fas fa-poll",
      description: "Build custom surveys with multiple question types. Send surveys via email. Receive real-time analytics and summary charts. Improve customer satisfaction and service quality.",
      seoKeywords: "customer survey tool, feedback analytics CRM"
    },
    {
      title: "Goal Tracking",
      icon: "fas fa-bullseye",
      description: "Set monthly or yearly targets. Track goal completion percentages. Assign goals to teams or individuals. Generate performance-based insights.",
      seoKeywords: "performance goals CRM, KPI tracking tool, business analytics"
    },
    {
      title: "Custom Fields",
      icon: "fas fa-edit",
      description: "Add custom fields to leads, clients, invoices, projects, tickets, etc. Tailor data collection to industry-specific needs. Improve data accuracy and reporting capability.",
      seoKeywords: "customizable CRM, CRM with custom fields"
    },
    {
      title: "Reporting & Analytics Dashboard",
      icon: "fas fa-chart-bar",
      description: "Sales reports, income vs. expenses charts. Lead conversion reports. Project profitability tracking. Staff activity and performance analysis. Financial forecasting tools.",
      seoKeywords: "CRM reporting system, analytics dashboard, business intelligence CRM"
    },
    {
      title: "Company Newsfeed",
      icon: "fas fa-newspaper",
      description: "Share internal announcements, updates, and documents. Improve team engagement and transparency.",
      seoKeywords: "company announcements system, internal communication CRM"
    },
    {
      title: "User & Staff Management",
      icon: "fas fa-user-shield",
      description: "Advanced role-based permissions. Staff-specific file directories. Designate admins, managers, and basic users. Activity logs for accountability.",
      seoKeywords: "user management CRM, staff permissions system"
    },
    {
      title: "Security & Permissions",
      icon: "fas fa-lock",
      description: "HTTPS enforcement for secure communication. Client permission control. Access restrictions on modules and files. GDPR-ready features for data protection.",
      seoKeywords: "secure CRM system, GDPR compliant CRM"
    },
    {
      title: "Updates & Version Enhancements",
      icon: "fas fa-sync-alt",
      description: "GDPR compliance. Email verification system. Google Drive file picker. Automated task reminders. UI/UX improvements for user friendliness.",
      seoKeywords: "CRM updates, CRM new features, modern CRM software"
    },
    {
      title: "SaaS Module (Optional Add-On)",
      icon: "fas fa-cloud",
      description: "Turn Acuit CRM into your own multi-tenant SaaS platform. Charge monthly/annual subscription fees. Create unlimited customer accounts. Offer tier-based pricing (Starter, Pro, Enterprise). Automate activation, suspension, and billing.",
      seoKeywords: "SaaS CRM Malaysia, white-label CRM system, multi-tenant CRM"
    }
  ];

  const whyChoose = [
    {
      title: "All-in-One Business Management Platform",
      description: "A single system to manage clients, projects, payments, proposals, and support."
    },
    {
      title: "Full Self-Hosted Control & Data Ownership",
      description: "Host Acuit CRM on your own server for maximum privacy."
    },
    {
      title: "Highly Customizable & Developer-Friendly",
      description: "Adapt the CRM to any industry or workflow through custom fields, plugins, and API integrations."
    },
    {
      title: "Cost-Effective One-Time Purchase",
      description: "Perfect for SMEs and freelancers who want premium features without monthly subscription fees."
    },
    {
      title: "Scalable for Freelancers, Startups & SMEs",
      description: "Start small, grow big — Acuit CRM supports unlimited users, clients, and projects."
    }
  ];

  return (
    <>
      <section className="page-banner bg-blue rel z-1" style={{backgroundImage: 'url(/assets/images/background/banner-bg.png)'}}>
        <div className="container">
          <div className="banner-inner">
            <h1 className="page-title wow fadeInUp delay-0-2s">Acuit CRM Solutions</h1>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb wow fadeInUp delay-0-4s">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item active">Solutions</li>
              </ol>
            </nav>
          </div>
        </div>
        <img className="dots-shape" src="/assets/images/shapes/white-dots-two.png" alt="Shape" />
        <img className="tringle-shape slideLeftRight" src="/assets/images/shapes/white-tringle.png" alt="Shape" />
        <img className="close-shape" src="/assets/images/shapes/white-close.png" alt="Shape" />
        <img src="/assets/images/newsletter/circle.png" alt="shape" className="banner-circle slideUpRight" />
        <img className="dots-shape-three slideUpDown delay-1-5s" src="/assets/images/shapes/white-dots-three.png" alt="Shape" />
      </section>

      <section className="about-page-section rel z-1 pt-130 rpt-100">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-xl-12">
              <div className="about-page-content rmb-65 wow fadeInUp delay-0-2s">
                <div className="section-title mb-25 text-center">
                  <span className="sub-title">Acuit CRM</span>
                  <h2>Robust Self-Hosted Customer Relationship Management System</h2>
                </div>
                <p className="text-justify text-center" style={{fontSize: '18px', maxWidth: '1000px', margin: '0 auto 30px'}}>
                  Acuit CRM is a robust, self-hosted Customer Relationship Management system designed to empower small businesses, freelancers, startups, and growing agencies. With its all-in-one platform, Acuit CRM seamlessly integrates client management, project tracking, invoicing, sales pipeline management, support ticketing, and business analytics—allowing organizations to run their entire business from a single dashboard.
                </p>
                <p className="text-justify text-center" style={{fontSize: '18px', maxWidth: '1000px', margin: '0 auto'}}>
                  Built for scalability, security, and customization, Acuit CRM offers companies the flexibility to tailor the system to their workflows while maintaining complete ownership of their data. Whether you're managing clients, handling projects, or generating invoices, Acuit CRM provides the tools necessary to boost productivity, streamline operations, and improve business performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="services-section-three bg-lighter rel z-1 pt-130 rpt-100 pb-100 rpb-70">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-xl-8 col-lg-10">
              <div className="section-title mb-55">
                <span className="sub-title">Key Features</span>
                <h2>Comprehensive CRM Features for Your Business</h2>
                <p>Discover 17 powerful features that help you manage every aspect of your business</p>
                <div className="mt-4">
                  <Link to="/features-detail" className="theme-btn">View Detailed Features <i className="fas fa-arrow-right"></i></Link>
                </div>
              </div>
            </div>
          </div>
          <div className="row solutions-cards-row">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="col-xl-4 col-md-6 mb-4">
                <div className="service-item wow fadeInUp solutions-card" style={{animationDelay: `${index * 0.1}s`}}>
                  <i className={feature.icon}></i>
                  <div className="content">
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <div className="mt-3" style={{marginTop: 'auto'}}>
                      <small className="text-muted">Keywords: {feature.seoKeywords}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="solutions-section-three text-white text-center rel bg-blue pt-130 rpt-100 z-1 pb-75 rpb-45" style={{backgroundImage: 'url(/assets/images/shapes/solutions-bg-dots.png)'}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-lg-10">
              <div className="section-title mb-75">
                <span className="sub-title">Why Choose Acuit CRM</span>
                <h2>All-in-One Business Management Platform</h2>
              </div>
            </div>
          </div>
          <div className="row justify-content-center solutions-why-choose-row">
            {whyChoose.map((item, index) => (
              <div key={index} className="col-xl-6 col-md-6 mb-4">
                <div className="solution-item-three wow fadeInUp solutions-why-choose-card" style={{animationDelay: `${index * 0.1}s`, textAlign: 'left', padding: '30px'}}>
                  <i className="fas fa-check-circle solutions-why-choose-icon" style={{fontSize: '24px'}}></i>
                  <h4>{item.title}</h4>
                  <p className="mt-2">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-section rel z-1 bg-gray pt-130 rpt-100 pb-100 rpb-70">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-lg-10">
              <div className="section-title text-center text-white mb-45">
                <span className="sub-title">Licensing & Pricing</span>
                <h2>Cost-Effective One-Time Purchase</h2>
                <p className="text-white mt-3">No monthly subscription fees. Own your CRM forever.</p>
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-6">
              <div className="pricing-item wow fadeInUp delay-0-2s" style={{height: '450px'}}>
                <h5 className="price-title">Regular License</h5>
                <span className="price">$65</span>
                <p>One-time purchase</p>
                <Link to="/pricing" className="theme-btn">Choose Package <i className="fas fa-long-arrow-alt-right"></i></Link>
                <ul className="list-style-one">
                  <li>Single site use</li>
                  <li>Lifetime updates</li>
                  <li>6 months support</li>
                  <li>Unlimited users (server capacity)</li>
                  <li>All 17 features included</li>
                </ul>
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="pricing-item wow fadeInUp delay-0-4s" style={{height: '450px'}}>
                <h5 className="price-title">Extended License</h5>
                <span className="price">$295</span>
                <p>One-time purchase</p>
                <Link to="/pricing" className="theme-btn">Choose Package <i className="fas fa-long-arrow-alt-right"></i></Link>
                <ul className="list-style-one">
                  <li>Multiple sites use</li>
                  <li>Lifetime updates</li>
                  <li>12 months support</li>
                  <li>Unlimited users (server capacity)</li>
                  <li>All 17 features included</li>
                  <li>SaaS module compatible</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row justify-content-center mt-50">
            <div className="col-xl-10">
              <div className="text-center text-white">
                <p style={{fontSize: '18px'}}>
                  <strong>Note:</strong> Supports unlimited users depending on server capacity. All licenses include lifetime updates and access to all 17 features. Perfect for SMEs and freelancers who want premium features without monthly subscription fees.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section-three rel z-1 pt-130 rpt-100 pb-130 rpb-100">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-xl-6 col-lg-6">
              <div className="about-content-three wow fadeInRight delay-0-2s">
                <div className="section-title mb-25">
                  <span className="sub-title">Get Started</span>
                  <h2>Ready to Transform Your Business?</h2>
                </div>
                <p className="text-justify">
                  Acuit CRM provides everything you need to manage your business efficiently. From lead management to invoicing, from project tracking to customer support - all in one integrated platform. Built for scalability, security, and customization.
                </p>
                <ul className="mt-30 mb-45">
                  <li><i className="fas fa-check"></i> 30-day free trial available</li>
                  <li><i className="fas fa-check"></i> Free demo and consultation</li>
                  <li><i className="fas fa-check"></i> Easy setup and migration</li>
                  <li><i className="fas fa-check"></i> Comprehensive documentation</li>
                  <li><i className="fas fa-check"></i> Dedicated support team</li>
                  <li><i className="fas fa-check"></i> Full self-hosted control</li>
                </ul>
                <Link to="/contact" className="theme-btn style-three">Get Started <i className="fas fa-arrow-right"></i></Link>
              </div>
            </div>
            <div className="col-xl-6 col-lg-6">
              <div className="about-image rmb-55 wow fadeInLeft delay-0-2s">
                <img src="/assets/images/about/about-two.png" alt="Get Started" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Solutions;
